
import { Injectable, signal, computed } from '@angular/core';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getFirestore, Firestore, collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc, deleteField, serverTimestamp, Timestamp, connectFirestoreEmulator, onSnapshot } from 'firebase/firestore';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getAuth, Auth, signInWithPopup, signInWithCustomToken, OAuthProvider, GoogleAuthProvider, signOut, onAuthStateChanged, User, getAdditionalUserInfo, connectAuthEmulator, signInAnonymously } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken, AppCheck } from 'firebase/app-check';

export interface ConsentRecord {
  policyVersion: string;
  termsVersion: string;
  acceptedAt: Timestamp | null;
  crossBorderTransfer: boolean;
  age18Plus: boolean;
}

export interface UserProfile {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
  isPro: boolean;
  role: string;
  // Email mirrored from Firebase Auth (added 2026-05-02). Backfilled on login
  // for legacy profiles. Null when the user declined the email scope on the
  // Twitch OAuth screen. Validated by Firestore rule against auth.token.email
  // — clients cannot spoof to a different address.
  email?: string | null;
  // Sync-related fields (added in v0.3.5 server-sync release)
  syncEnabled?: boolean;
  migrationCompleted?: boolean;
  schemaVersion?: number;
  lastSyncedAt?: Timestamp | null;
  // Consent record (added 2026-05-02). Missing on legacy profiles → soft prompt.
  consent?: ConsentRecord;
  // Queued consent written to Firestore when auth-rf was unreachable. Synced
  // to РФ Postgres on next successful auth-rf contact (handleUserLogin).
  pendingConsent?: { policyVersion: string; termsVersion: string; crossBorderTransfer: boolean; age18Plus: boolean } | null;
}

// Schema version baseline for the cloud-sync feature. Bump on incompatible
// changes; SyncService inspects this to decide whether to run migrations.
export const SYNC_SCHEMA_VERSION = 2;

// Current versions of legal documents. Bump on substantive change → existing
// users will be re-prompted to accept the new revision via the consent banner.
export const CURRENT_POLICY_VERSION = '1.2';
export const CURRENT_TERMS_VERSION = '1.0';

// Hard deadline for legacy users to accept the consent. Before this date,
// missing/outdated consent shows a dismissable banner. After it, a blocking
// modal — using the service requires acceptance.
export const CONSENT_GRACE_DEADLINE = new Date('2026-06-01T00:00:00Z');

// System-wide banner pushed by admins via auth-rf POST /admin/broadcast →
// Firestore system/banner. Shape mirrored in src/models/admin.types.ts.
export interface SystemBanner {
  message: string;
  severity: 'info' | 'warning';
  expiresAt: string;       // ISO8601
  updatedAt?: string;
  updatedBy?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  app: any | null = null;
  firestore: Firestore | null = null;
  functions: Functions | null = null;
  auth: Auth | null = null;
  appCheck: AppCheck | null = null;

  // URLs to Cloud Run services
  cloudRunApiUrl: string | null = null;    // Lightweight API (chat, Twitch API, Gemini, webhooks)
  // URL of the РФ-localized auth backend (handles Twitch OAuth and primary
  // recording of identity PD in Yandex Postgres before any Google service
  // sees the user — the whole point of the Path A architecture).
  authRfUrl: string = 'https://auth.peakster.su';
  // Login path. 'auth-rf' = Path A redirect (normal). 'firebase-popup' =
  // emergency fallback while the auth-rf VM is unreachable — env.js
  // AUTH_LOGIN_MODE, see root CLAUDE.md «Аварийный режим входа».
  loginMode: 'auth-rf' | 'firebase-popup' = 'auth-rf';
  cloudRunWorkerUrl: string | null = null; // Heavy worker (clip/zip downloads)

  isInitialized = signal(false);
  initError = signal<string | null>(null);

  // True when the URL contains `?emulators=1` — connects Auth/Firestore/Functions
  // to the local Firebase Emulator Suite. Dev-only: never enabled in prod.
  useEmulators = signal(this.detectEmulators());

  private detectEmulators(): boolean {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('emulators') === '1';
  }

  // Auth State
  currentUser = signal<User | null>(null);
  userProfile = signal<UserProfile | null>(null);
  isAuthLoading = signal(true);

  // System banner — populated via onSnapshot on Firestore system/banner doc.
  // Null when no active broadcast (or expired). Consumed by app.component for
  // top-of-page strip rendering. Updates pushed by admin panel are picked up
  // here in near-real-time.
  systemBanner = signal<SystemBanner | null>(null);

  isPro = computed(() => this.userProfile()?.isPro === true);
  isSyncEligible = computed(() => {
    const p = this.userProfile();
    return !!p && p.isPro === true && p.syncEnabled !== false;
  });

  // True when the logged-in user has either no consent record at all (legacy
  // pre-2026-05-02 profile) or accepted a now-outdated revision. Drives both
  // the dismissable banner and (after CONSENT_GRACE_DEADLINE) the blocking modal.
  needsConsentRefresh = computed(() => {
    const p = this.userProfile();
    if (!p) return false;
    const c = p.consent;
    if (c && c.policyVersion === CURRENT_POLICY_VERSION && c.termsVersion === CURRENT_TERMS_VERSION) return false;
    // Firestore fallback: consent queued while auth-rf was down counts as accepted.
    const pc = p.pendingConsent;
    if (pc && pc.policyVersion === CURRENT_POLICY_VERSION && pc.termsVersion === CURRENT_TERMS_VERSION) return false;
    return true;
  });

  // Re-evaluated only when dependencies change. Sufficient because the
  // deadline is days-scale; users opening the app after the cutoff get the
  // blocking modal on next paint.
  isConsentBlocking = computed(() => {
    return this.needsConsentRefresh() && Date.now() >= CONSENT_GRACE_DEADLINE.getTime();
  });

  constructor() {
    this.initFirebase();
  }

  private initFirebase() {
    try {
      // Безопасный доступ к process.env в браузере
      const env = (typeof window !== 'undefined' ? (window as any).process?.env : {}) || {};

      // FALLBACK VALUES
      const config = {
        apiKey: env.FIREBASE_API_KEY || "AIzaSyDyt75nesOJb69cWAiaEwGOexTDht92R4A",
        authDomain: env.FIREBASE_AUTH_DOMAIN || "twitch-analyzer-v2-f6457.firebaseapp.com",
        projectId: env.FIREBASE_PROJECT_ID || "twitch-analyzer-v2-f6457",
        storageBucket: env.FIREBASE_STORAGE_BUCKET || "twitch-analyzer-v2-f6457.firebasestorage.app",
        messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID || "236701976274",
        appId: env.FIREBASE_APP_ID || "1:236701976274:web:fad6d002ac2757540f8f0b"
      };

      this.cloudRunApiUrl = env.CLOUD_RUN_API_URL || "https://twitch-api-service-226426699260.europe-west3.run.app";
      this.cloudRunWorkerUrl = env.CLOUD_RUN_WORKER_URL || "https://twitch-worker-service-226426699260.europe-west3.run.app";
      this.authRfUrl = env.AUTH_RF_URL || "https://auth.peakster.su";
      this.loginMode = env.AUTH_LOGIN_MODE === 'firebase-popup' ? 'firebase-popup' : 'auth-rf';

      if (!config.apiKey || config.apiKey.includes('PASTE_')) {
        console.warn('Firebase API Key не найден. Функции облака будут недоступны.');
        return;
      }

      // 1. Initialize App
      // Check if already initialized to prevent duplicate app errors
      if (getApps().length > 0) {
        this.app = getApp();
      } else {
        this.app = initializeApp(config);
      }

      // 1b. Initialize App Check — only if a reCAPTCHA Enterprise key is configured.
      // Empty key (default for fresh installs / dev) → skip silently; backend
      // accepts requests without X-Firebase-AppCheck header in log-only mode.
      // Once enforced (APPCHECK_REQUIRED=1 on Cloud Run), unconfigured frontend
      // would 401 — that's intentional, forces explicit setup.
      const appCheckKey = env.RECAPTCHA_ENTERPRISE_KEY;
      if (appCheckKey && !this.useEmulators()) {
        try {
          this.appCheck = initializeAppCheck(this.app, {
            provider: new ReCaptchaEnterpriseProvider(appCheckKey),
            isTokenAutoRefreshEnabled: true,
          });
        } catch (acErr: any) {
          console.warn('App Check initialization failed (continuing without):', acErr.message);
          this.appCheck = null;
        }
      }

      // 2. Initialize Firestore (Independently try-catch to allow app to run if DB is blocked)
      try {
        this.firestore = getFirestore(this.app);
        if (this.useEmulators() && this.firestore) {
          connectFirestoreEmulator(this.firestore, '127.0.0.1', 8080);
          console.warn('🔧 Firestore connected to EMULATOR at 127.0.0.1:8080');
        }
        // Subscribe to system banner doc — broadcast by auth-rf admin endpoint.
        // No auth required (rules: allow read: if true). Filters expired /
        // null-message states down to a clean signal value for app.component.
        if (this.firestore) {
          onSnapshot(doc(this.firestore, 'system', 'banner'), (snap) => {
            if (!snap.exists()) { this.systemBanner.set(null); return; }
            const data = snap.data() as Partial<SystemBanner>;
            if (!data.message || !data.expiresAt) { this.systemBanner.set(null); return; }
            try {
              if (Date.parse(data.expiresAt) < Date.now()) { this.systemBanner.set(null); return; }
            } catch { this.systemBanner.set(null); return; }
            this.systemBanner.set({
              message: data.message,
              severity: data.severity === 'warning' ? 'warning' : 'info',
              expiresAt: data.expiresAt,
              updatedAt: data.updatedAt,
              updatedBy: data.updatedBy,
            });
          }, (err) => {
            console.warn('system/banner subscription failed:', err.message);
          });
        }
      } catch (dbErr: any) {
        console.warn('Firestore initialization failed (Project possibly blocked or offline):', dbErr.message);
      }

      // 3. Initialize Functions (Independently try-catch)
      try {
        this.functions = getFunctions(this.app, 'europe-west3');
        if (this.useEmulators() && this.functions) {
          connectFunctionsEmulator(this.functions, '127.0.0.1', 5001);
          console.warn('🔧 Functions connected to EMULATOR at 127.0.0.1:5001');
        }
      } catch (fnErr: any) {
        console.warn('Functions initialization failed:', fnErr.message);
      }

      // 4. Initialize Auth
      try {
        this.auth = getAuth(this.app);
        if (this.useEmulators() && this.auth) {
          connectAuthEmulator(this.auth, 'http://127.0.0.1:9099', { disableWarnings: true });
          console.warn('🔧 Auth connected to EMULATOR at 127.0.0.1:9099');
        }
        // Listen to auth state
        onAuthStateChanged(this.auth, async (user) => {
          this.currentUser.set(user);
          if (user) {
            await this.handleUserLogin(user);
          } else {
            this.userProfile.set(null);
          }
          this.isAuthLoading.set(false);
        });
      } catch (authErr: any) {
        console.warn('Auth initialization failed:', authErr.message);
        this.isAuthLoading.set(false);
      }

      this.isInitialized.set(true);

    } catch (e) {
      // Critical failure only if App cannot init
      console.error('Критическая ошибка инициализации Firebase:', e);
      this.initError.set(e instanceof Error ? e.message : 'Unknown error');
      // Even if Firebase fails hard, we mark initialized so UI doesn't hang forever
      // (Features relying on it will just fail gracefully later)
      this.isInitialized.set(false);
      this.isAuthLoading.set(false);
    }
  }

  // --- AUTHENTICATION ---

  /**
   * Path A login flow — redirects to auth-rf at https://auth.peakster.su,
   * which handles Twitch OAuth + primary identity recording in РФ Postgres
   * BEFORE any Firebase service sees the user. After the redirect dance,
   * auth-rf returns a Firebase custom token in the URL fragment (#ct=…).
   *
   * The browser bootstrap in app.component.ts detects the fragment and calls
   * completeRfAuth() below to trade the custom token for a Firebase ID token
   * via signInWithCustomToken. From that point on, Firestore + Cloud Run +
   * onSnapshot subscriptions all work unchanged — Firebase Auth becomes a
   * pure token issuer, never an identity broker.
   *
   * Disaster-recovery fallback: env.js AUTH_LOGIN_MODE='firebase-popup' →
   * signInWithPopup via the Firebase Twitch OIDC provider (config kept
   * active specifically for this). Skips РФ primary recording — emergency
   * only, see root CLAUDE.md «Аварийный режим входа».
   */
  async loginWithTwitch(consentGiven: boolean = false) {
    if (!this.auth) throw new Error('Auth is not initialized');
    // Stash consent intent so handleUserLogin can record it after sign-in.
    sessionStorage.setItem('rf_consent_pending', consentGiven ? '1' : '0');
    if (this.loginMode === 'firebase-popup') {
      const provider = new OAuthProvider('oidc.twitch');
      provider.addScope('openid');
      try {
        // Same uid as the auth-rf path only for accounts with oidc.twitch
        // linked in Firebase Auth (all pre-Path-A users). onAuthStateChanged →
        // handleUserLogin does the rest; consent falls back to pendingConsent.
        await signInWithPopup(this.auth, provider);
      } catch (error) {
        sessionStorage.removeItem('rf_consent_pending');
        throw error;
      }
      return;
    }
    const returnUrl = encodeURIComponent(window.location.origin + '/');
    const consentParam = consentGiven ? '&consent=1' : '';
    window.location.assign(`${this.authRfUrl}/auth/twitch/start?return_url=${returnUrl}${consentParam}`);
  }

  /**
   * Called from app.component.ts when the browser lands on /#ct=… after the
   * auth-rf redirect. Trades the custom token for a Firebase ID token; after
   * that, onAuthStateChanged fires → handleUserLogin → fetchProfileFromAuthRf
   * populates email/displayName/photoURL into the userProfile signal.
   */
  async completeRfAuth(customToken: string): Promise<void> {
    if (!this.auth) throw new Error('Auth is not initialized');
    try {
      await signInWithCustomToken(this.auth, customToken);
      // onAuthStateChanged will handle the rest. Strip the fragment so a
      // refresh doesn't replay the (now-consumed) token.
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('completeRfAuth failed:', error);
      sessionStorage.removeItem('rf_consent_pending');
      throw error;
    }
  }

  /**
   * Fetch the authoritative identity record (email, displayName, photoURL,
   * consent state) from the РФ Postgres via auth-rf. Called from handleUserLogin
   * on every sign-in event (initial + page reload), so the in-memory profile
   * always reflects the source of truth.
   *
   * Uses /auth/profile-firebase which validates the user's existing Firebase
   * ID token via firebase-admin — single-token model in the browser.
   */
  private async fetchProfileFromAuthRf(): Promise<{ email: string | null, displayName: string | null, photoURL: string | null, consent: ConsentRecord | null } | null> {
    if (!this.auth?.currentUser) return null;
    try {
      const idToken = await this.auth.currentUser.getIdToken();
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), 4000);
      const resp = await fetch(`${this.authRfUrl}/auth/profile-firebase`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
        signal: ac.signal,
      }).finally(() => clearTimeout(timer));
      if (!resp.ok) {
        console.warn('auth-rf /auth/profile-firebase returned HTTP', resp.status);
        return null;
      }
      const profile = await resp.json();
      return {
        email: profile.email ?? null,
        displayName: profile.display_name ?? null,
        photoURL: profile.photo_url ?? null,
        consent: profile.consent_accepted_at ? {
          policyVersion: profile.consent_policy_version,
          termsVersion: profile.consent_terms_version,
          acceptedAt: null, // server-side timestamp; client doesn't need precise value
          crossBorderTransfer: !!profile.consent_cross_border,
          age18Plus: !!profile.consent_age18,
        } : null,
      };
    } catch (e) {
      console.warn('fetchProfileFromAuthRf failed:', e);
      return null;
    }
  }

  async loginWithGoogle() {
    if (!this.auth) throw new Error('Auth is not initialized');
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(this.auth, provider);
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  }

  /** Dev-only: anonymous sign-in for emulator testing. UID is generated by Firebase. */
  async loginAnonymousDev() {
    if (!this.auth) throw new Error('Auth is not initialized');
    try {
      await signInAnonymously(this.auth);
    } catch (error) {
      console.error('Anonymous sign-in failed:', error);
      throw error;
    }
  }

  async logout() {
    if (!this.auth) return;
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  /**
   * Force a Firebase ID token refresh. Used by the admin panel after a Pro/role
   * change to make new claims take effect immediately on the affected user's
   * NEXT request — without waiting up to 1h for natural token TTL. Note: this
   * only refreshes the LOCAL user's token. To force-refresh a different user's
   * claims, the admin endpoint /admin/users/:uid/refresh-claims is what does it
   * (revokes their refresh tokens server-side).
   */
  async forceRefreshIdToken(): Promise<void> {
    if (!this.auth?.currentUser) return;
    try { await this.auth.currentUser.getIdToken(true); }
    catch (err) { console.warn('forceRefreshIdToken failed:', err); }
  }

  /**
   * Returns a current App Check token (or `null` if App Check isn't initialized
   * — e.g. RECAPTCHA_ENTERPRISE_KEY empty, emulator mode, or initial-load
   * failure). Caller attaches as `X-Firebase-AppCheck` header on Cloud Run
   * fetches. Backend operates in log-only mode by default, so `null` is OK
   * during rollout.
   */
  async getAppCheckToken(): Promise<string | null> {
    if (!this.appCheck) return null;
    try {
      const result = await getToken(this.appCheck, /* forceRefresh */ false);
      return result.token;
    } catch (err: any) {
      console.warn('getAppCheckToken failed:', err?.message || err);
      return null;
    }
  }

  /**
   * Builds the standard set of headers for fetches against Cloud Run services
   * (twitch-api-service, twitch-worker-service). Returns null when the user
   * isn't authenticated — caller should bail with a "log in first" error.
   * Includes Firebase ID token (Authorization) and App Check token if
   * available (X-Firebase-AppCheck). Omits Content-Type so the caller can
   * set it on the fetch init.
   */
  async buildCloudRunHeaders(): Promise<Record<string, string> | null> {
    const idToken = await this.auth?.currentUser?.getIdToken();
    if (!idToken) return null;
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${idToken}`,
    };
    const appCheckToken = await this.getAppCheckToken();
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
    return headers;
  }

  /**
   * Path A: Firestore now stores ONLY operational fields (uid, isPro, role,
   * syncEnabled, migrationCompleted, schemaVersion, telemetry counters).
   * Identity PD (email, displayName, photoURL, consent_*) is fetched live from
   * the РФ Postgres via auth-rf — that's the compliance anchor for ст.18 ч.5
   * 152-ФЗ. The in-memory userProfile signal merges both sources so consumer
   * components don't need to know which field comes from where.
   */
  private async handleUserLogin(user: User) {
    if (!this.firestore) return;

    // Fetch identity (РФ Postgres) and operational record (Firestore) in parallel.
    const [identity, userDocSnap] = await Promise.all([
      this.fetchProfileFromAuthRf(),
      getDoc(doc(this.firestore, 'users', user.uid)),
    ]);

    try {
      const userDocRef = doc(this.firestore, 'users', user.uid);

      if (userDocSnap.exists()) {
        const operational = userDocSnap.data() as UserProfile;
        // Merge: operational fields from Firestore + identity fields from auth-rf.
        // If auth-rf is unreachable (identity === null), fall back to whatever
        // legacy email/displayName/photoURL might still be in Firestore (during
        // the migration window) — better stale data than no data.
        this.userProfile.set({
          ...operational,
          email: identity?.email ?? operational.email ?? null,
          displayName: identity?.displayName ?? operational.displayName ?? null,
          photoURL: identity?.photoURL ?? operational.photoURL ?? null,
          consent: identity?.consent ?? operational.consent,
        });

        // If auth-rf is reachable and Firestore has a queued pending consent,
        // sync it to РФ Postgres now (fire-and-forget).
        if (identity !== null && operational.pendingConsent) {
          const pc = operational.pendingConsent;
          if (pc.policyVersion === CURRENT_POLICY_VERSION && pc.termsVersion === CURRENT_TERMS_VERSION) {
            this.syncPendingConsent(user.uid, pc).catch(() => {});
          }
        }
      } else {
        // First login on this device → create bare operational record (no PD).
        // Identity already lives in РФ Postgres (created server-side by
        // auth-rf during /auth/twitch/callback). Email/displayName/photoURL
        // are NOT written here — Firestore must never become a parallel
        // identity store.
        const operationalDbData = {
          uid: user.uid,
          isPro: false,
          role: 'user',
          syncEnabled: true,
          migrationCompleted: false,
          schemaVersion: SYNC_SCHEMA_VERSION,
          streamCount: 0,
          zipCount: 0,
          createdAt: serverTimestamp(),
        };
        await setDoc(userDocRef, operationalDbData);

        const newProfile: UserProfile = {
          uid: user.uid,
          displayName: identity?.displayName ?? null,
          photoURL: identity?.photoURL ?? null,
          email: identity?.email ?? null,
          isPro: false,
          role: 'user',
          syncEnabled: true,
          migrationCompleted: false,
          schemaVersion: SYNC_SCHEMA_VERSION,
          lastSyncedAt: null,
          consent: identity?.consent ?? undefined,
        };
        this.userProfile.set(newProfile);
      }

      // If this sign-in came from a fresh Twitch login with the consent
      // checkbox ticked, complete the consent record now (Path A: written to
      // РФ Postgres by recordConsent → /auth/consent-firebase). Cleared after
      // use to avoid re-recording on subsequent page loads.
      if (sessionStorage.getItem('rf_consent_pending') === '1') {
        sessionStorage.removeItem('rf_consent_pending');
        // Don't await — fire-and-forget so login flow completes promptly.
        // recordConsent surfaces errors via console.error.
        this.recordConsent().catch(() => {});
      }
    } catch (error) {
      console.error('Error handling user login database step:', error);
    }
  }

  /**
   * Records (or refreshes) the consent acceptance on the current user profile.
   * Called when:
   *  - a legacy user clicks "Согласен" in the post-login consent banner, or
   *  - any user re-accepts after a policy/terms version bump.
   * Writes serverTimestamp() so the recorded date is authoritative (not local clock).
   */
  async recordConsent(): Promise<void> {
    const uid = this.currentUser()?.uid;
    if (!uid || !this.auth?.currentUser) return;
    try {
      // Path A: consent is the legal record for ст.6 152-ФЗ basis of processing.
      // It belongs in the same store as primary identity (РФ Postgres), not
      // duplicated in Firestore. Use /auth/consent-firebase which validates
      // the user's existing Firebase ID token via firebase-admin and writes
      // to users.consent_* columns in PG.
      const idToken = await this.auth.currentUser.getIdToken();
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), 4000);
      const resp = await fetch(`${this.authRfUrl}/auth/consent-firebase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          policy_version: CURRENT_POLICY_VERSION,
          terms_version: CURRENT_TERMS_VERSION,
          cross_border: true,
          age18: true,
        }),
        signal: ac.signal,
      }).finally(() => clearTimeout(timer));
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '');
        throw new Error(`auth-rf /auth/consent-firebase HTTP ${resp.status}: ${errBody}`);
      }

      const current = this.userProfile();
      if (current) {
        // Mirror in the in-memory signal so needsConsentRefresh flips false
        // immediately — next handleUserLogin call (page reload) will refetch
        // the authoritative timestamp from РФ Postgres.
        this.userProfile.set({
          ...current,
          consent: {
            policyVersion: CURRENT_POLICY_VERSION,
            termsVersion: CURRENT_TERMS_VERSION,
            acceptedAt: null,
            crossBorderTransfer: true,
            age18Plus: true,
          },
          pendingConsent: null,
        });
      }
    } catch (err) {
      // auth-rf unreachable — queue consent in Firestore. Will be synced to
      // РФ Postgres on next successful login when auth-rf is available.
      console.warn('recordConsent via auth-rf failed, queuing to Firestore:', err);
      if (!this.firestore) throw err;
      const pendingConsent = {
        policyVersion: CURRENT_POLICY_VERSION,
        termsVersion: CURRENT_TERMS_VERSION,
        crossBorderTransfer: true,
        age18Plus: true,
      };
      // Throws on Firestore failure — caller (acceptConsent) shows the alert.
      await setDoc(doc(this.firestore, 'users', uid), { pendingConsent }, { merge: true });
      const current = this.userProfile();
      if (current) {
        this.userProfile.set({ ...current, pendingConsent });
      }
    }
  }

  /** Sync queued Firestore consent to РФ Postgres and clear the pending flag. */
  private async syncPendingConsent(uid: string, pc: NonNullable<UserProfile['pendingConsent']>): Promise<void> {
    if (!this.auth?.currentUser || !this.firestore) return;
    try {
      const idToken = await this.auth.currentUser.getIdToken();
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), 4000);
      const resp = await fetch(`${this.authRfUrl}/auth/consent-firebase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_version: pc.policyVersion,
          terms_version: pc.termsVersion,
          cross_border: pc.crossBorderTransfer,
          age18: pc.age18Plus,
        }),
        signal: ac.signal,
      }).finally(() => clearTimeout(timer));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Clear pending from Firestore and update in-memory signal.
      await updateDoc(doc(this.firestore, 'users', uid), { pendingConsent: deleteField() });
      const current = this.userProfile();
      if (current) {
        this.userProfile.set({
          ...current,
          consent: {
            policyVersion: pc.policyVersion,
            termsVersion: pc.termsVersion,
            acceptedAt: null,
            crossBorderTransfer: pc.crossBorderTransfer,
            age18Plus: pc.age18Plus,
          },
          pendingConsent: null,
        });
      }
    } catch (e) {
      console.warn('syncPendingConsent failed (will retry on next login):', e);
    }
  }

  /**
   * Mark cloud-sync first-time migration as complete without actually migrating.
   * Used when the user explicitly declines the prompt ("Никогда") or when there
   * is nothing local to migrate (auto-skip path).
   */
  async markMigrationCompleted(): Promise<void> {
    const uid = this.currentUser()?.uid;
    if (!uid || !this.firestore) return;
    try {
      await setDoc(
        doc(this.firestore, 'users', uid),
        {
          migrationCompleted: true,
          schemaVersion: SYNC_SCHEMA_VERSION,
          syncEnabled: true,
        },
        { merge: true }
      );
      const current = this.userProfile();
      if (current) {
        this.userProfile.set({
          ...current,
          migrationCompleted: true,
          schemaVersion: SYNC_SCHEMA_VERSION,
          syncEnabled: true,
        });
      }
    } catch (err) {
      console.error('markMigrationCompleted failed:', err);
    }
  }

  // --- DATA FETCHING ---

  /**
   * Fetches stream markers within a wall-clock time window for the current
   * authenticated user. Reads from the per-user subcollection
   * `users/{uid}/markers` populated by paired StreamMarkers copies.
   *
   * Returned objects include `sourceId` so callers can show which copy emitted
   * each marker and apply per-source filters.
   */
  async fetchStreamMarkers(startTime: Date, endTime: Date): Promise<any[]> {
    if (!this.firestore) {
      console.warn("Firestore is not initialized.");
      return [];
    }
    const uid = this.currentUser()?.uid;
    if (!uid) return [];

    try {
      const markersRef = collection(this.firestore, "users", uid, "markers");
      const q = query(
        markersRef,
        where("utcTime", ">=", startTime.toISOString()),
        where("utcTime", "<=", endTime.toISOString())
      );

      const querySnapshot = await getDocs(q);
      const markers: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as any;
        markers.push({
          // Backward-compat shape for existing call sites (timestamp, time, tag).
          timestamp: data.utcTime,
          time: data.time,
          tag: data.tag,
          // New fields:
          sourceId: data.sourceId,
          markerId: data.markerId ?? doc.id,
        });
      });
      console.log(`Fetched ${markers.length} stream markers from users/${uid}/markers for this window.`);
      return markers;
    } catch (err) {
      console.error("Failed to fetch stream markers:", err);
      return [];
    }
  }
}
